import _ from 'lodash';
import { Column } from 'primereact/column';
import { TreeTable } from 'primereact/treetable';
import { useEffect, useMemo, useState } from 'react';
import {
  COUNTRIES,
  DEFAULT_CALCULATION_MULTIPLIER,
  DEFAULT_FACTOR,
  EMPTY_FIELD_PLACEHOLDER,
  LeistungsSlices
} from '../../../Helper/Statics/Constants';
import {
  generatePriceValidUntilKey,
  simpleGenerateBlFactorId
} from '../../../Helper/Util/IdGeneratorHelper';
import BeeArrowDiff from '../../Atoms/BeeArrowDiff';
import BeeContentHeadline from '../../Atoms/BeeContentHeadline';
import BeeLoadingSpinner from '../../Atoms/BeeLoadingSpinner';
import { TimelineStep } from '../../Atoms/BeeTimeline';
import { calculateDiff } from './AlphaDLVerlaufEPTable';
import './AlphaDLVerlaufFaktorTable.scss';
import BeeNumberInput from '../../Atoms/BeeNumberInput';

type AlphaDLVerlaufFaktorTableProps = {
  dataLoading: boolean;
  phases: TimelineStep[];
  valueLookup: Map<string, any>;
  providerId: string;
};

export default function AlphaDLVerlaufFaktorTable({
  dataLoading,
  phases,

  valueLookup,
  providerId
}: AlphaDLVerlaufFaktorTableProps) {
  const [setupComplete, setSetupComplete] = useState<boolean>(false);
  const blStructure = useMemo(() => setupBlData(), []);
  const [columns, setColumns] = useState<any>([]);

  useEffect(() => {
    setSetupComplete(dataLoading);
  }, [dataLoading]);

  useEffect(() => {
    let cols: any = [];
    if (phases) {
      phases.forEach((p: TimelineStep) => {
        cols.push({
          id: p.id,
          header: p.title,
          period:
            new Date(p.start).toLocaleDateString('de-DE') +
            ' - ' +
            new Date(p.end).toLocaleDateString('de-DE')
        });
      });
    }
    setColumns(cols);
  }, [phases]);

  ///////////////////////
  //////// LOGIC ////////
  ///////////////////////

  function setupBlData() {
    let pay: any = [];
    COUNTRIES.forEach((country) => {
      let cObj: any = {
        key: country.id,
        data: {
          type: 'cat_1',
          title: country.country
        },
        children: []
      };
      country.regions.forEach((r) => {
        let rObj: any = {
          key: r.id,
          data: {
            type: 'cat_2',
            title: r.region
          },
          children: []
        };
        LeistungsSlices.forEach((sl) => {
          let curr: any = _.cloneDeep(sl);
          curr.regionId = r.id;
          curr.type = 'pos';
          rObj.children.push({
            key: sl.id,
            data: curr
          });
        });
        cObj.children.push(rObj);
      });
      pay.push(cObj);
    });
    return pay;
  }

  function createBlFactorKey(node: any, phaseId: string) {
    if (node.data && node.data.type === 'pos') {
      const regionId: String = node.data.regionId;
      const sliceId: String = node.data.id;
      return simpleGenerateBlFactorId(phaseId, regionId, sliceId);
    }
    return '';
  }

  function search(lookupKey: string) {
    return valueLookup.get(lookupKey);
  }

  ///////////////////////
  /// VIEW COMPONENTS ///
  ///////////////////////

  const blFactorValueTemplate = (node: any, column: any) => {
    if (!setupComplete) {
      return <BeeLoadingSpinner strokeWidth={'3'} type={'primary'} />;
    }
    const phaseId: string = column.props.header.key;
    const phase: TimelineStep | undefined = _.find(phases, function (p) {
      return p.id === phaseId;
    });
    if (node.data) {
      if (node.data.type === 'pos' || node.data.type === 'cat_2') {
        //test user participation
        if (!phase || !_.includes(phase.userIds, providerId)) {
          return <div>Keine Teilnahme</div>;
        }

        //if position
        if (node.data.type === 'pos') {
          const v = search(createBlFactorKey(node, phaseId));
          let value = v ? parseInt(v) : DEFAULT_FACTOR;
          value = value ? value : DEFAULT_FACTOR;
          let rootValue = -1;
          let rootPId = null;
          let rootPTitle = null;
          for (let i = 0; i < columns.length; i++) {
            //search first valid participation
            const p: TimelineStep | undefined = _.find(phases, function (p) {
              return p.id === columns[i].id;
            });
            if (p && _.includes(p.userIds, providerId)) {
              const tmp = search(createBlFactorKey(node, columns[i].id));
              rootValue = tmp ? parseInt(tmp) : DEFAULT_FACTOR;
              rootValue = rootValue ? rootValue : DEFAULT_FACTOR;
              rootPId = columns[i].id;
              rootPTitle = columns[i].header;
              break;
            }
          }
          return (
            <div className={'alpha-dl-factorOverTime-blFac-value'}>
              <div className={'alpha-dl-factorOverTime-blFac-num'}>
                <BeeNumberInput
                  value={
                    value
                      ? _.divide(value, DEFAULT_CALCULATION_MULTIPLIER)
                      : value === 0
                      ? 0
                      : undefined
                  }
                  grouping={false}
                  readOnly={true}
                />
              </div>
              {rootPId ? (
                <BeeArrowDiff
                  value={calculateDiff(rootValue, value)}
                  tooltip={'In Bezug auf Phase: ' + rootPTitle}
                  disabled={false}
                  isRoot={phaseId === rootPId}
                />
              ) : null}
            </div>
          );
        } else if (node.data.type === 'cat_2') {
          let value: number = 0;
          let rootValue = 0;
          let rootPId = null;
          let rootPTitle = null;
          if (node.children) {
            let pointer = -1;
            //search first valid participation
            for (let i = 0; i < columns.length; i++) {
              const p: TimelineStep | undefined = _.find(phases, function (p) {
                return p.id === columns[i].id;
              });
              //search first valid participation
              if (p && _.includes(p.userIds, providerId)) {
                pointer = i;
                break;
              }
            }
            node.children.forEach((child: any) => {
              let val = search(createBlFactorKey(child, phaseId));
              value += val && parseInt(val) ? parseInt(val) : DEFAULT_FACTOR;
              if (pointer !== -1) {
                let val = search(createBlFactorKey(child, columns[pointer].id));
                rootValue +=
                  val && parseInt(val) ? parseInt(val) : DEFAULT_FACTOR;
                rootPId = columns[pointer].id;
                rootPTitle = columns[pointer].header;
              }
            });
          }
          return (
            <div className={'alpha-dl-factorOverTime-blFac-value'}>
              <div className={'alpha-dl-factorOverTime-blFac-num'}>
                <BeeNumberInput
                  value={
                    value
                      ? _.divide(
                          _.round(value / node.children.length),
                          DEFAULT_CALCULATION_MULTIPLIER
                        )
                      : value === 0
                      ? 0
                      : undefined
                  }
                  grouping={false}
                  readOnly={true}
                />
              </div>
              {rootPId ? (
                <BeeArrowDiff
                  value={calculateDiff(rootValue, value)}
                  tooltip={'In Bezug auf Phase: ' + rootPTitle}
                  disabled={false}
                  isRoot={phaseId === rootPId}
                />
              ) : null}
            </div>
          );
        }
      }
    }
    return <div />;
  };

  const dynamicRegionColumns = columns
    ? columns.map((col: any, i: any) => {
        const effectiveDate = valueLookup.get(
          generatePriceValidUntilKey(col.id) //phaseId
        );
        return (
          <Column
            key={col.id + '_' + col.header}
            style={{ width: '9em' }}
            className={'alpha-dl-factorOverTime-blFac-col'}
            body={blFactorValueTemplate}
            header={
              <div key={col.id}>
                <div>{col.header}</div>
                <div className={'phase-period'}>{col.period}</div>
                <div className={'phase-effectiveDate pt-3'}>
                  Gültig bis:{' '}
                  {effectiveDate
                    ? new Date(effectiveDate).toLocaleDateString('de-DE')
                    : EMPTY_FIELD_PLACEHOLDER}
                </div>
              </div>
            }
          />
        );
      })
    : null;

  return (
    <div className={'alpha-dl-factorOverTime'}>
      <div className="mt-5">
        <BeeContentHeadline
          label={'Bundesländer'}
          headline={'h2'}
          type={'secondary'}
        />
        <div className="mb-3">
          Folgende Übersicht zeigt die Entwicklung der Bundeslandfaktoren des
          ausgewählten Dienstleisters über die Angebotsphasen hinweg. Von dem
          Dienstleister nicht angegebene Faktoren werden mit 1,0 (100%)
          ausgewertet.
        </div>

        <TreeTable
          value={blStructure}
          scrollable
          style={{ width: '100%' }}
          className={'alpha-dl-factorOverTime-table'}
        >
          <Column
            field="title"
            header="Bundesland"
            className={'alpha-dl-factorOverTime-bl-col frozen-col'}
            expander
            style={{ width: '20em' }}
          />
          {dynamicRegionColumns}
        </TreeTable>
      </div>
    </div>
  );
}
